"use client";

import { useState, useCallback } from "react";

import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio";
import { OrbCarouselContent } from "@ui/components/demos/SliderGenerateTTSDemo/OrbCarouselContent";
import { OrbCarouselFooter } from "@ui/components/demos/SliderGenerateTTSDemo/OrbCarouselFooter";
import { getPreviewUrlForLanguage, type VoiceWithPreview } from "@ui/components/demos/VoicesWithTTS/types";

// Scale orbs so the center orb is 16rem, matching the agents orb (tw-w-64)
const SCALE = 16 / 17.625;

const OMNIBOX_DESKTOP_SLOTS = {
  hiddenLeft: { offsetRem: -47.5 * SCALE, sizeRem: 7.5 * SCALE, topRem: 4.5 * SCALE, opacity: 0, zIndex: 0 },
  farLeft: { offsetRem: -36.75 * SCALE, sizeRem: 10 * SCALE, topRem: 3.75 * SCALE, opacity: 0.5, zIndex: 1 },
  left: { offsetRem: -21.375 * SCALE, sizeRem: 13.875 * SCALE, topRem: 1.875 * SCALE, opacity: 0.8, zIndex: 2 },
  center: { offsetRem: 0, sizeRem: 16, topRem: 0, opacity: 1, zIndex: 3 },
  right: { offsetRem: 21.375 * SCALE, sizeRem: 13.875 * SCALE, topRem: 1.875 * SCALE, opacity: 0.8, zIndex: 2 },
  farRight: { offsetRem: 36.75 * SCALE, sizeRem: 10 * SCALE, topRem: 3.75 * SCALE, opacity: 0.5, zIndex: 1 },
  hiddenRight: { offsetRem: 47.5 * SCALE, sizeRem: 7.5 * SCALE, topRem: 4.5 * SCALE, opacity: 0, zIndex: 0 },
};

function getSlideDirection(currentIndex: number, nextIndex: number): 1 | -1 {
  return nextIndex > currentIndex ? 1 : -1;
}

interface SpeechOrbProps {
  voices: VoiceWithPreview[];
  locale: string;
}

function SpeechOrbInner({ voices, locale }: SpeechOrbProps) {
  const { play, togglePlay, playingId, isPaused, stop } = useNativeAudio();
  const [activeIndex, setActiveIndex] = useState(() => Math.floor(voices.length / 2));
  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);

  const playVoiceAtIndex = useCallback(
    (index: number) => {
      const voice = voices[index];
      if (!voice) return;
      void play({
        id: voice.voiceId,
        url: getPreviewUrlForLanguage(voice, locale),
        trackingProps: { source: "omnibox_speech_orb", voice_id: voice.voiceId, position: index },
      });
    },
    [play, locale, voices],
  );

  const scrollPrev = useCallback(() => {
    const nextIndex = Math.max(0, activeIndex - 1);
    if (nextIndex === activeIndex) return;
    setSlideDirection(-1);
    if (playingId && !isPaused) {
      playVoiceAtIndex(nextIndex);
    } else {
      stop();
    }
    setActiveIndex(nextIndex);
  }, [activeIndex, isPaused, playVoiceAtIndex, playingId, stop]);

  const scrollNext = useCallback(() => {
    const nextIndex = Math.min(voices.length - 1, activeIndex + 1);
    if (nextIndex === activeIndex) return;
    setSlideDirection(1);
    if (playingId && !isPaused) {
      playVoiceAtIndex(nextIndex);
    } else {
      stop();
    }
    setActiveIndex(nextIndex);
  }, [activeIndex, isPaused, playVoiceAtIndex, playingId, stop, voices.length]);

  const handleSelectOrbIndex = useCallback(
    (index: number) => {
      setSlideDirection(getSlideDirection(activeIndex, index));
      setActiveIndex(index);
      playVoiceAtIndex(index);
    },
    [activeIndex, playVoiceAtIndex],
  );

  const handleToggleActiveVoice = useCallback(() => {
    const voice = voices[activeIndex];
    if (!voice) return;
    void togglePlay({
      id: voice.voiceId,
      url: getPreviewUrlForLanguage(voice, locale),
      trackingProps: { source: "omnibox_speech_orb", voice_id: voice.voiceId, position: activeIndex },
    });
  }, [activeIndex, locale, togglePlay, voices]);

  return (
    <div className='tw-relative tw-mx-auto tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center'>
      <div className='tw-relative tw-min-h-0 -tw-mx-4 tw-w-[calc(100%+2rem)] sm:-tw-mx-6 sm:tw-w-[calc(100%+3rem)]'>
        <OrbCarouselContent
          voices={voices}
          activeIndex={activeIndex}
          playingId={playingId}
          isPaused={isPaused}
          onSelectOrbIndex={handleSelectOrbIndex}
          onToggleActiveVoice={handleToggleActiveVoice}
          onPrevious={scrollPrev}
          onNext={scrollNext}
          desktopSlots={OMNIBOX_DESKTOP_SLOTS}
          desktopStageHeight='18rem'
          mobileStageHeight='14rem'
          centered
        />
        <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-hidden md:tw-block tw-w-44 tw-bg-gradient-to-r tw-from-cream tw-to-transparent tw-z-10' />
        <div className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-hidden md:tw-block tw-w-44 tw-bg-gradient-to-l tw-from-cream tw-to-transparent tw-z-10' />
      </div>
      <OrbCarouselFooter
        voices={voices}
        activeIndex={activeIndex}
        slideDirection={slideDirection}
        onPrevious={scrollPrev}
        onNext={scrollNext}
        hideUseVoiceCta
        variant='compact'
        summaryCardWidthPx={Math.round(260 * SCALE)}
        summaryCardGapPx={Math.round(80 * SCALE)}
      />
    </div>
  );
}

export function SpeechOrb(props: SpeechOrbProps) {
  return (
    <NativeAudioProvider>
      <SpeechOrbInner {...props} />
    </NativeAudioProvider>
  );
}
